import { useState, useEffect } from 'react';
import { Modal, Select, Spinner, Banner, BlockStack } from '@shopify/polaris';
import { getRequest } from '../../../../shared/components/functions.jsx';
import { STATUS_LABELS } from '../../../../config/roasterySpec';
import DiffSummaryCard from './DiffSummaryCard.jsx';
import DiffAreaDetail from './DiffAreaDetail.jsx';
import DiffLineChanges from './DiffLineChanges.jsx';

export default function QuotationDiffModal({ open, onClose, quotations, idA, idB }) {
  const [selectedA, setSelectedA] = useState(idA);
  const [selectedB, setSelectedB] = useState(idB);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync props when modal opens
  useEffect(() => {
    if (open) {
      setSelectedA(idA);
      setSelectedB(idB);
      setDiff(null);
      setError(null);
    }
  }, [open, idA, idB]);

  // Fetch diff when selections change
  useEffect(() => {
    if (!open || !selectedA || !selectedB || selectedA === selectedB) {
      setDiff(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getRequest(`/api/project-quotations/compare/${selectedA}/${selectedB}`)
      .then((data) => {
        if (cancelled) return;
        if (data?.message) {
          setError(data.message);
          setDiff(null);
        } else {
          setDiff(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load comparison');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedA, selectedB]);

  const options = (quotations || []).map((q) => ({
    label: `v${q.VersionNumber}${q.VersionLabel ? ` — ${q.VersionLabel}` : ''} (${STATUS_LABELS[q.Status] || 'Unknown'})`,
    value: String(q.ID)
  }));

  return (
    <Modal open={open} onClose={onClose} title="Compare Quotation Versions" large>
      <Modal.Section>
        <BlockStack gap="400">
          {/* Version selectors */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Select label="Base (A)" options={options} value={selectedA ? String(selectedA) : ''} onChange={(val) => setSelectedA(Number(val))} />
            <Select label="Compare (B)" options={options} value={selectedB ? String(selectedB) : ''} onChange={(val) => setSelectedB(Number(val))} />
          </div>

          {selectedA === selectedB && selectedA && <Banner tone="warning">Select two different versions to compare.</Banner>}

          {error && <Banner tone="critical">{error}</Banner>}

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner size="large" />
            </div>
          )}

          {diff && !loading && (
            <BlockStack gap="400">
              <DiffSummaryCard summary={diff.summary} versionA={diff.quotationA.VersionNumber} versionB={diff.quotationB.VersionNumber} />

              <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                <DiffAreaDetail byArea={diff.byArea} />
              </div>

              <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                <DiffLineChanges changes={diff.changes} />
              </div>
            </BlockStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
